import type { MessageRouter } from "@pdfking/functionpool"
import type { AnyGUID, AsyncSequence, StringGUID } from "@pdfking/tfunc"
import type { Engine } from "../types/Engine"
import type { EngineOptions } from "../types/EngineOptions"
import type { TaskMap, TrackingInterface } from "../types/TaskMap"
import type {
  WireOutput,
  WireRequest,
  WireResponse,
  WireTracking,
} from "../types/WireTypes"
import { CachedForward } from "@pdfking/functionpool"
import {
  Channel,
  CreateSingleton,
  GuidToString,
  IterableToArray,
  JSONEncode,
  MapAsync,
  Peek,
  ProcessIterable,
  SafeIterable,
  StringHash,
} from "@pdfking/tfunc"

const emit_callbacks = <TM extends TaskMap>(
  forward: MessageRouter<WireRequest<TM>, WireResponse>,
  tracking: TrackingInterface | undefined
): MessageRouter<WireRequest<TM>, WireResponse> =>
  async function* (original) {
    const { stream: input, value } = await Peek(original)
    const guid = (value as { task_id: StringGUID }).task_id

    tracking?.start(guid)

    const { error, stream } = SafeIterable(forward(input))

    for await (const response of stream) {
      if (response.enum === "tracking") {
        const { enum: _, task_id: _id, ...tracked } = response as WireTracking
        tracking?.push(guid, tracked)
      } else {
        yield response
      }
    }

    tracking?.stop(guid, await error)

    const e = await error
    if (e) throw e
  }

const resolve_task_id = async <TM extends TaskMap>(
  input: AsyncGenerator<WireRequest<TM>>
): Promise<{
  cache_evict?: boolean
  guid: StringGUID
  input: AsyncGenerator<WireRequest<TM>>
}> => {
  const { stream, value } = await Peek(input)
  const first = value as { task_id: StringGUID; cache_evict?: boolean }
  return {
    cache_evict: first.cache_evict,
    guid: first.task_id,
    input: stream,
  }
}

const build_request = async function* <TM extends TaskMap>(
  task_name: string,
  props: Record<string, unknown> & {
    readonly task_id?: AnyGUID
    readonly cache_evict?: boolean
  },
  input: AsyncSequence<unknown>
): AsyncGenerator<WireRequest<TM>> {
  const stream = MapAsync(
    value => ({ enum: "input", value }) as WireRequest<TM>,
    input
  )

  const { task_id, cache_evict, ...rest } = props

  if (task_id) {
    yield {
      enum: task_name,
      ...rest,
      cache_evict,
      task_id: GuidToString(task_id),
    } as unknown as WireRequest<TM>
    yield* stream
    return
  }

  // No task_id — collect all input, hash to produce deterministic id
  const collected = await IterableToArray(stream)

  const hash_id = await StringHash([
    JSONEncode({
      enum: task_name,
      ...rest,
      input: collected,
    }),
  ])

  yield {
    enum: task_name,
    ...rest,
    cache_evict,
    task_id: GuidToString(hash_id),
  } as unknown as WireRequest<TM>
  yield* collected
}

export const EngineMaker = <TM extends TaskMap>(
  factory: (
    forward: MessageRouter<WireRequest<TM>, WireResponse>
  ) => MessageRouter<WireRequest<TM>, WireResponse>,
  options?: EngineOptions
): Engine<TM> => {
  const router = CreateSingleton(() => factory(forward))

  const forward: MessageRouter<WireRequest<TM>, WireResponse> = CachedForward(
    emit_callbacks(t => router()(t), options?.tracking),
    resolve_task_id,
    options?.cache
  )

  return new Proxy({} as Engine<TM>, {
    get: (_, task_name: string) =>
      async function* (
        props: Record<string, unknown> & {
          readonly task_id?: AnyGUID
          readonly cache_evict?: boolean
        },
        input?: AsyncSequence<unknown>
      ) {
        const { push, stop, stream } = Channel<unknown>()

        void ProcessIterable(
          forward(build_request(task_name, props, input ?? [])),
          response => push((response as WireOutput).value),
          stop
        )

        yield* stream
      },
  })
}
